/**
 * Single source of truth for the GigMi fee shown before a contract charge (the
 * "Contract Amount Details" / confirm-charges modal). Under Stripe the fee is a
 * server-resolved percent OR fixed value, so the UI must do ZERO fee math: it fetches
 * `payment/fee-breakdown` (mirrors mobile's `useFeeBreakdown`) and renders the server's
 * dollar fee, total, and admin-authored explanation verbatim. The displayed total then
 * always equals the actual charge. Under the legacy provider (or on fetch failure) it
 * falls back to the flat `gigmiCharges` value already in Redux — never blocking the UI.
 */
import { useState, useEffect, useCallback } from "react";
import { isStripe } from "../utils/paymentProvider";
import { getFeeBreakdown } from "../api/stripeApi/feeBreakdown";

const toMoney = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Legacy flat-fee shape: fee is already dollars, total is a local sum. */
export const legacyBreakdown = (flatAmount, legacyFee) => {
  const flat = toMoney(flatAmount);
  const fee = toMoney(legacyFee);
  return {
    flatAmount: flat,
    gigmiFee: fee,
    total: Number((flat + fee).toFixed(2)),
    explanation: null,
    isStripeSourced: false,
  };
};

/** Stripe shape: server sends integer cents + explanation; convert cents→dollars. */
export const stripeBreakdown = (data) => ({
  flatAmount: toMoney(data?.flat_amount) / 100,
  gigmiFee: toMoney(data?.gigmi_fee) / 100,
  total: toMoney(data?.total) / 100,
  explanation: data?.explanation ?? null,
  isStripeSourced: true,
});

/**
 * @param {Object} args
 * @param {number} [args.contractId] persisted contract key (contract mode).
 * @param {number|string} args.flatAmount contract amount (dollars) — legacy total AND
 *   the base for amount-mode (converted to cents at the API boundary).
 * @param {string} [args.flowKey] "homeowner"|"contractor" — the payer role, enabling the
 *   stateless amount-mode quote before a contract exists (quick-contract CREATE).
 * @param {number|string} args.legacyFee flat `gigmiCharges` fallback (dollars).
 * @param {boolean} [args.enabled=true] gate the fetch (fire only when the modal opens).
 */
export const useFeeBreakdown = ({
  contractId,
  flatAmount,
  flowKey,
  legacyFee,
  enabled = true,
}) => {
  // Stripe can quote from a persisted contractId OR a stateless (amount + flowKey).
  const canQuote = contractId || (flatAmount != null && flowKey);
  // Under Stripe the true fee is server-resolved (percent OR fixed), so seed
  // `null` and wait for `payment/fee-breakdown` — never seed the legacy flat cut,
  // which would briefly render the WRONG fee (e.g. a raw 10 shown as $10 for a
  // 10% fee) before the fetch lands. Legacy provider seeds the flat value.
  const [breakdown, setBreakdown] = useState(() =>
    isStripe() ? null : legacyBreakdown(flatAmount, legacyFee)
  );
  const [loading, setLoading] = useState(() => isStripe());

  const load = useCallback(async () => {
    if (!(isStripe() && canQuote)) {
      setBreakdown(legacyBreakdown(flatAmount, legacyFee));
      setLoading(false);
      return;
    }
    setLoading(true);
    // contract mode when a contractId exists, else the stateless amount-mode quote
    // ({amount cents, flowKey}). getFeeBreakdown picks the body from these fields.
    const data = await getFeeBreakdown(
      contractId
        ? { contractId }
        : { amount: Math.round(toMoney(flatAmount) * 100), flowKey }
    );
    // On fetch failure keep waiting (null) rather than showing the legacy flat
    // cut as if it were the Stripe fee — display must equal the actual charge.
    setBreakdown(data ? stripeBreakdown(data) : null);
    setLoading(false);
  }, [contractId, flatAmount, flowKey, legacyFee, canQuote]);

  useEffect(() => {
    if (enabled) {
      load();
    }
  }, [enabled, load]);

  return { breakdown, loading, refetch: load };
};

export default useFeeBreakdown;
